import { FileNode } from '@/lib/fileUtils';
import { useTranslation } from 'react-i18next';
import NewProjectModal from '../NewProjectModal';
import ConfirmDialog from '../ConfirmDialog';
import InstructionsModal from './modals/InstructionsModal';
import CreateFolderModal from './modals/CreateFolderModal';
import EditFolderModal from './modals/EditFolderModal';
import MoveFilesModal from './modals/MoveFilesModal';
import FileConflictModal from './modals/FileConflictModal';
import RenameDialog from '../Sidebar/RenameDialog';
import AddTextContentModal from './modals/AddTextContentModal';
import { showToast } from '../Toast';
import { ConflictFilesState } from '@/hooks/file/useFileUploadWithProgress';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'default' | 'destructive';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ProjectModalsProps {
  projectId: string;
  projectName: string;
  projectDescribe: string;

  // Instructions modal
  isInstructionsModalOpen: boolean;
  setIsInstructionsModalOpen: (open: boolean) => void;
  tempInstructions: string;
  setTempInstructions: (instructions: string) => void;
  onSaveInstructions: () => void;
  isSavingInstructions: boolean;

  // Create folder modal
  isCreateFolderModalOpen: boolean;
  setIsCreateFolderModalOpen: (open: boolean) => void;
  newFolderName: string;
  setNewFolderName: (name: string) => void;
  newFolderNameValidation: { valid: boolean; error: string };
  onCreateFolder: () => void;
  isCreatingFolder: boolean;

  // Edit folder modal
  isEditFolderModalOpen: boolean;
  setIsEditFolderModalOpen: (open: boolean) => void;
  editFolderName: string;
  setEditFolderName: (name: string) => void;
  editFolderNameValidation: { valid: boolean; error: string };
  editingFolder: FileNode | null;
  setEditingFolder: (folder: FileNode | null) => void;
  onEditFolder: () => void;
  isEditingFolder: boolean;

  // Move modal
  isMoveModalOpen: boolean;
  setIsMoveModalOpen: (open: boolean) => void;
  moveModalPath: FileNode[];
  setMoveModalPath: React.Dispatch<React.SetStateAction<FileNode[]>>;
  moveModalCurrentNodes: FileNode[];
  moveTargetPath: string | null;
  onMoveSelectFolder: (folder: FileNode) => void;
  onMoveConfirm: () => Promise<void>;
  isMoving: boolean;

  // File/Folder confirm dialog
  fileConfirmDialogProps: ConfirmDialogProps;

  // Edit project modal
  isEditProjectModalOpen: boolean;
  setIsEditProjectModalOpen: (open: boolean) => void;
  instructions: string;
  setInstructions: (instructions: string) => void;
  onUpdateProject?: (project: {
    id: string;
    name: string;
    description: string;
    instructions: string;
  }) => void;

  // Rename conversation modal
  showConversationRenameModal: boolean;
  setShowConversationRenameModal: (show: boolean) => void;
  renameValue: string;
  setRenameValue: (value: string) => void;
  onRenameConversation: (newName?: string) => void;
  isRenamingConversation: boolean;
  setRenameConversationId: (id: string | null) => void;

  // Conversation confirm dialog
  conversationDialogProps: ConfirmDialogProps;

  // File conflict modal
  isConflictModalOpen: boolean;
  conflictFiles: ConflictFilesState | null;
  isCheckingConflicts: boolean;
  onConflictCancel: () => void;
  onConflictSkip: () => Promise<void>;
  onConflictOverwrite: () => Promise<void>;

  // File preview - 已移到 ProjectDetail 主布局中，使用分屏模式

  // Add text content modal
  isAddTextContentModalOpen: boolean;
  setIsAddTextContentModalOpen: (open: boolean) => void;
  addTextContentTitle: string;
  setAddTextContentTitle: (title: string) => void;
  addTextContentContent: string;
  setAddTextContentContent: (content: string) => void;
  onAddTextContent: () => void;
  isAddingTextContent: boolean;
}

export default function ProjectModals({
  projectId,
  projectName,
  projectDescribe,

  // Instructions modal
  isInstructionsModalOpen,
  setIsInstructionsModalOpen,
  tempInstructions,
  setTempInstructions,
  onSaveInstructions,
  isSavingInstructions,

  // Create folder modal
  isCreateFolderModalOpen,
  setIsCreateFolderModalOpen,
  newFolderName,
  setNewFolderName,
  newFolderNameValidation,
  onCreateFolder,
  isCreatingFolder,

  // Edit folder modal
  isEditFolderModalOpen,
  setIsEditFolderModalOpen,
  editFolderName,
  setEditFolderName,
  editFolderNameValidation,
  editingFolder,
  setEditingFolder,
  onEditFolder,
  isEditingFolder,

  // Move modal
  isMoveModalOpen,
  setIsMoveModalOpen,
  moveModalPath,
  setMoveModalPath,
  moveModalCurrentNodes,
  moveTargetPath,
  onMoveSelectFolder,
  onMoveConfirm,
  isMoving,

  // File/Folder confirm dialog
  fileConfirmDialogProps,

  // Edit project modal
  isEditProjectModalOpen,
  setIsEditProjectModalOpen,
  instructions,
  setInstructions,
  onUpdateProject,

  // Rename conversation modal
  showConversationRenameModal,
  setShowConversationRenameModal,
  renameValue,
  setRenameValue,
  onRenameConversation,
  isRenamingConversation,
  setRenameConversationId,

  // Conversation confirm dialog
  conversationDialogProps,

  // File conflict modal
  isConflictModalOpen,
  conflictFiles,
  isCheckingConflicts,
  onConflictCancel,
  onConflictSkip,
  onConflictOverwrite,

  // Add text content modal
  isAddTextContentModalOpen,
  setIsAddTextContentModalOpen,
  addTextContentTitle,
  setAddTextContentTitle,
  addTextContentContent,
  setAddTextContentContent,
  onAddTextContent,
  isAddingTextContent,
}: ProjectModalsProps) {
  const { t } = useTranslation();
  return (
    <>
      {/* Instructions Modal */}
      <InstructionsModal
        isOpen={isInstructionsModalOpen}
        onOpenChange={setIsInstructionsModalOpen}
        projectName={projectName}
        tempInstructions={tempInstructions}
        onInstructionsChange={setTempInstructions}
        onSave={onSaveInstructions}
        isSaving={isSavingInstructions}
      />

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onOpenChange={setIsCreateFolderModalOpen}
        folderName={newFolderName}
        onFolderNameChange={setNewFolderName}
        validation={{
          valid: newFolderNameValidation.valid,
          error: newFolderNameValidation.error || null,
        }}
        onCreate={onCreateFolder}
        isCreating={isCreatingFolder}
      />

      {/* Edit Folder Modal */}
      <EditFolderModal
        isOpen={isEditFolderModalOpen}
        onOpenChange={setIsEditFolderModalOpen}
        folderName={editFolderName}
        onFolderNameChange={setEditFolderName}
        validation={{
          valid: editFolderNameValidation.valid,
          error: editFolderNameValidation.error || null,
        }}
        editingFolder={editingFolder}
        onEdit={onEditFolder}
        onClose={() => {
          setIsEditFolderModalOpen(false);
          setEditingFolder(null);
          setEditFolderName('');
        }}
        isEditing={isEditingFolder}
      />

      {/* Move Modal */}
      <MoveFilesModal
        isOpen={isMoveModalOpen}
        onOpenChange={setIsMoveModalOpen}
        moveModalPath={moveModalPath}
        onPathChange={setMoveModalPath}
        moveModalCurrentNodes={moveModalCurrentNodes}
        moveTargetPath={moveTargetPath}
        onSelectFolder={onMoveSelectFolder}
        onConfirm={onMoveConfirm}
        isMoving={isMoving}
      />

      {/* File/Folder Confirm Dialog (delete folder, delete file, batch delete) */}
      <ConfirmDialog {...fileConfirmDialogProps} />

      {/* Edit Project Modal */}
      <NewProjectModal
        isOpen={isEditProjectModalOpen}
        onClose={() => setIsEditProjectModalOpen(false)}
        mode="edit"
        projectData={{
          id: projectId,
          name: projectName,
          description: projectDescribe,
          instructions,
        }}
        onUpdateProject={updatedProject => {
          // Update local instructions state
          setInstructions(updatedProject.instructions);
          // Call parent's update callback (parent will refresh project data)
          onUpdateProject?.(updatedProject);
        }}
        onShowToast={(message, type, _variant) => {
          showToast(message, type);
        }}
      />

      {/* Rename Conversation Modal */}
      <RenameDialog
        isOpen={showConversationRenameModal}
        title={t('dialog.renameConversation')}
        currentName={renameValue}
        isLoading={isRenamingConversation}
        onConfirm={(newName: string) => {
          onRenameConversation(newName);
        }}
        onCancel={() => {
          if (isRenamingConversation) return;
          setShowConversationRenameModal(false);
          setRenameConversationId(null);
          setRenameValue('');
        }}
      />

      {/* Conversation Confirm Dialog (bulk delete and single delete) */}
      <ConfirmDialog {...conversationDialogProps} />

      {/* File Conflict Confirmation Dialog */}
      <FileConflictModal
        isOpen={isConflictModalOpen && !!conflictFiles}
        conflictFiles={
          conflictFiles
            ? {
                conflicting: conflictFiles.conflicting,
                nonConflicting: conflictFiles.nonConflicting,
              }
            : null
        }
        isChecking={isCheckingConflicts}
        onCancel={onConflictCancel}
        onSkip={onConflictSkip}
        onOverwrite={onConflictOverwrite}
      />

      {/* File Preview - 已移到 ProjectDetail 主布局中，使用分屏模式 */}

      {/* Add Text Content Modal */}
      <AddTextContentModal
        isOpen={isAddTextContentModalOpen}
        onOpenChange={setIsAddTextContentModalOpen}
        title={addTextContentTitle}
        onTitleChange={setAddTextContentTitle}
        content={addTextContentContent}
        onContentChange={setAddTextContentContent}
        onAdd={onAddTextContent}
        isAdding={isAddingTextContent}
      />
    </>
  );
}
